import { Controller } from "@hotwired/stimulus"

// "When to Show It" in the field settings slideover (TASK-267.7).
//
// Always | Only when…, then a sentence: Show when [field] [operator] [answer].
// The server renders the right state; this keeps it right as the author
// edits: switching the source field rebuilds the operator list for its kind
// and swaps the answer between a dropdown of that field's own options, an
// On/Off dropdown, or a text box. Only enabled controls submit, so exactly
// one of choice/text posts form_field[logic][visible_if][value], and the
// hidden "clear" input posts an empty logic while the rule is off.
export default class extends Controller {
  static targets = ["mode", "panel", "clear", "source", "operator", "choice", "text"]
  static values = { sources: Array, operators: Object, labels: Object }

  connect() {
    if (this.hasSourceTarget) this.refresh({ rebuildChoices: false })
  }

  get only() {
    const picked = this.modeTargets.find((radio) => radio.checked)
    return picked?.value === "only"
  }

  get source() {
    return this.sourcesValue.find((s) => s.key === this.sourceTarget.value)
  }

  modeChanged() {
    this.panelTarget.classList.toggle("hidden", !this.only)
    this.clearTarget.disabled = this.only
    this.refresh({ rebuildChoices: false })
  }

  sourceChanged() {
    this.textTarget.value = ""
    this.refresh({ rebuildChoices: true })
  }

  operatorChanged() {
    this.refresh({ rebuildChoices: false })
  }

  refresh({ rebuildChoices }) {
    const only = this.only
    this.sourceTarget.disabled = !only
    this.operatorTarget.disabled = !only

    const source = this.source
    this.rebuildOperators(source?.kind || "text")

    const operator = this.operatorTarget.value
    const answerNeeded = only && !["is_empty", "is_not_empty"].includes(operator)
    const choices = source?.choices || null

    if (choices) this.rebuildChoices(choices, rebuildChoices)

    this.show(this.choiceTarget, answerNeeded && !!choices)
    this.show(this.textTarget, answerNeeded && !choices)
  }

  // Keep the current operator when the new kind still offers it (or it is a
  // legacy value already stored); otherwise fall to the kind's first.
  rebuildOperators(kind) {
    const current = this.operatorTarget.value
    const offered = this.operatorsValue[kind] || this.operatorsValue.text
    const options = offered.slice()
    if (current && !options.some(([, value]) => value === current)) {
      options.push([this.labelsValue[current] || current, current])
    }

    const same = options.length === this.operatorTarget.options.length &&
      options.every(([, value], i) => this.operatorTarget.options[i].value === value)
    if (!same) {
      this.operatorTarget.replaceChildren(...options.map(([text, value]) => new Option(text, value)))
    }
    const keep = options.some(([, value]) => value === current)
    this.operatorTarget.value = keep ? current : options[0][1]
  }

  rebuildChoices(choices, force) {
    const select = this.choiceTarget
    const current = force ? "" : select.value
    const options = choices.slice()
    if (current && !options.some(([, value]) => value === current)) {
      options.push([`${current} (not an option now)`, current])
    }

    const same = options.length === select.options.length &&
      options.every(([, value], i) => select.options[i].value === value)
    if (!same) select.replaceChildren(...options.map(([text, value]) => new Option(text, value)))
    if (current) select.value = current
  }

  show(element, visible) {
    element.classList.toggle("hidden", !visible)
    element.disabled = !visible
  }
}
